import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isSupportedLocale, pickDefaultLocale } from "@/i18n/locale-utils";

type State =
  | { kind: "loading" }
  | { kind: "anon" }
  | { kind: "needs_onboarding" }
  | { kind: "ok" };

/**
 * Gates routes that require authentication. Optionally enforces onboarding
 * completion: if the user has not finished onboarding and they're not already
 * on an /onboarding/* route, redirect them to /onboarding/persona.
 */
export function RequireAuth({
  children,
  requireOnboarding = true,
}: {
  children: ReactNode;
  requireOnboarding?: boolean;
}) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const { pathname, search, hash } = useLocation();
  const { locale: paramLocale } = useParams<{ locale: string }>();
  const locale = isSupportedLocale(paramLocale) ? paramLocale : pickDefaultLocale();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) {
        if (mounted) setState({ kind: "anon" });
        return;
      }
      if (!requireOnboarding) {
        if (mounted) setState({ kind: "ok" });
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_completed_at")
        .eq("id", u.user.id)
        .maybeSingle();
      if (!mounted) return;
      const done = !!profile?.onboarding_completed_at;
      if (!done && !pathname.includes("/onboarding")) {
        setState({ kind: "needs_onboarding" });
      } else {
        setState({ kind: "ok" });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [pathname, requireOnboarding]);

  if (state.kind === "loading") {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">Loading…</div>
    );
  }
  if (state.kind === "anon") {
    const dest = `${pathname}${search}${hash}`;
    return <Navigate to={`/${locale}/login?next=${encodeURIComponent(dest)}`} replace state={{ from: dest }} />;
  }
  if (state.kind === "needs_onboarding") return <Navigate to={`/${locale}/onboarding/persona`} replace />;
  return <>{children}</>;
}
